import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ProductStatusTypes } from '../../../../../../common/enums';
import {
  showErrorToast,
  showSuccessToast,
} from '../../../../../../common/global/helpers';
import Button from '../../../../../../common/widgets/Button';
import { GridCellTd } from '../../../../../../common/widgets/Grid';
import PopoverDialog from '../../../../../../common/widgets/PopoverDialog';
import { lowStockThreshold } from '../../../../constants';
import { CodeSourceTypes, ProductTypes } from '../../../../enums';
import { ProductItemModel } from '../../../../models/products';
import { resources } from '../../../../resources/resources';
import { productsService } from '../../../../services/products';

export const StockDropdown = (props: { product: ProductItemModel }) => {
  const { product } = props;
  const { t: translate } = useTranslation();

  if (product.codeSourceType === CodeSourceTypes.Generated)
    return (
      <div>{translate(resources.products.list.stockStatus.automatic)}</div>
    );

  return <div>{translate(resources.products.list.stockStatus.manual)}</div>;
};

export const getStockCell = (product: ProductItemModel) => {
  if (
    _.isNil(product.stock) ||
    (product.productTypeId !== ProductTypes.ProductOrService &&
      product.productTypeId !== ProductTypes.GiftCard)
  ) {
    return (
      <GridCellTd className="grid-center">
        <strong>-</strong>
      </GridCellTd>
    );
  }
  return (
    <GridCellTd
      className={`grid-center stock-no ${
        product.stock <= lowStockThreshold ? 'stock-low' : ''
      }`}
    >
      {!_.isNil(product.codeSourceType) ? (
        <PopoverDialog
          className="m-tooltip"
          type="hover"
          placement="bottom"
          closeFromContent
          appendTo={document.body}
          content={<StockDropdown product={product} />}
        >
          <strong>{product.stock}</strong>
        </PopoverDialog>
      ) : (
        <strong>{product.stock}</strong>
      )}
    </GridCellTd>
  );
};

export const ActionsDropdown = (props: {
  product: ProductItemModel;
  showEditWarningDialog: () => void;
  showDeactivateProductDialog: () => void;
  showCancelApprovalDialog: () => void;
  showCancelDeactivationDialog: () => void;
  showSendProductForApprovalDialog: () => void;
  showInvalidProductForApprovalDialog: () => void;
  showDeleteDraftDialog: () => void;
}) => {
  const {
    product,
    showEditWarningDialog,
    showDeactivateProductDialog,
    showCancelApprovalDialog,
    showCancelDeactivationDialog,
    showSendProductForApprovalDialog,
    showInvalidProductForApprovalDialog,
    showDeleteDraftDialog,
  } = props;
  const { t: translate } = useTranslation();
  const navigate = useNavigate();

  const showEdit =
    product.statusId === ProductStatusTypes.Active ||
    product.statusId === ProductStatusTypes.Draft ||
    product.statusId === ProductStatusTypes.Rejected;

  const showAddStock =
    (product.statusId === ProductStatusTypes.Draft ||
      product.statusId === ProductStatusTypes.PendingApproval ||
      product.statusId === ProductStatusTypes.Active) &&
    (product.productTypeId === ProductTypes.ProductOrService ||
      product.productTypeId === ProductTypes.GiftCard);

  const showDuplicate =
    product.statusId === ProductStatusTypes.Draft ||
    product.statusId === ProductStatusTypes.PendingApproval ||
    product.statusId === ProductStatusTypes.Active ||
    product.statusId === ProductStatusTypes.Rejected ||
    product.statusId === ProductStatusTypes.PendingDeactivation ||
    product.statusId === ProductStatusTypes.Archived;

  const showSendForApproval = product.statusId === ProductStatusTypes.Draft;

  const showCancelApproval =
    product.statusId === ProductStatusTypes.PendingApproval;

  const showDeactivate = product.statusId === ProductStatusTypes.Active;

  const showCancelDeactivationRequest =
    product.statusId === ProductStatusTypes.PendingDeactivation;

  const showDeleteDraft =
    product.statusId === ProductStatusTypes.Draft && product.isDeletable;

  const onEditClick = () => {
    if (product.statusId === ProductStatusTypes.Active) {
      showEditWarningDialog();
    } else {
      navigate(`/products/edit/${product.productId}`);
    }
  };

  const onAddStockClick = () => {
    navigate(`/products/add/codes/${product.productId}`);
  };

  const onDeactivateClick = () => {
    showDeactivateProductDialog();
  };

  const onDuplicateClick = async () => {
    const resp = await productsService.duplicateProduct(product.productId);
    if (resp.hasErrors) {
      showErrorToast(translate(resources.errors.generic));
    } else {
      showSuccessToast(translate(resources.products.duplicate.success));
      navigate(`/products/edit/${resp.data}`);
    }
  };

  const onSendForApprovalClick = async () => {
    const resp = await productsService.validateProductApproval(
      product.productId,
    );
    if (resp.hasErrors) return;
    if (resp.data) {
      showSendProductForApprovalDialog();
    } else {
      showInvalidProductForApprovalDialog();
    }
  };

  const onCancelApprovalClick = () => {
    showCancelApprovalDialog();
  };

  const onCancelDeactivationRequestClick = () => {
    showCancelDeactivationDialog();
  };

  const onDeleteDraftClick = () => {
    showDeleteDraftDialog();
  };

  return (
    <>
      {showEdit && (
        <Button className="btn" onClick={onEditClick}>
          {translate(resources.products.list.actions.edit)}
        </Button>
      )}
      {showAddStock && (
        <Button className="btn" onClick={onAddStockClick}>
          {translate(resources.products.list.actions.addStock)}
        </Button>
      )}
      {showDuplicate && (
        <Button className="btn" onClick={onDuplicateClick}>
          {translate(resources.products.list.actions.duplicate)}
        </Button>
      )}
      {showSendForApproval && (
        <Button className="btn" onClick={onSendForApprovalClick}>
          {translate(resources.products.list.actions.sendForApproval)}
        </Button>
      )}
      {showCancelApproval && (
        <Button className="btn red-btn" onClick={onCancelApprovalClick}>
          {translate(resources.products.list.actions.cancelApproval)}
        </Button>
      )}
      {showDeactivate && (
        <Button className="btn red-btn" onClick={onDeactivateClick}>
          {translate(resources.products.list.actions.deactivate)}
        </Button>
      )}
      {showCancelDeactivationRequest && (
        <Button
          className="btn red-btn"
          onClick={onCancelDeactivationRequestClick}
        >
          {translate(resources.products.list.actions.cancelDeactivationRequest)}
        </Button>
      )}
      {showDeleteDraft && (
        <Button className="btn red-btn" onClick={onDeleteDraftClick}>
          {translate(resources.products.list.actions.deleteDraft)}
        </Button>
      )}
    </>
  );
};
